'use client';

import { useState, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { Link } from '@/components/LocaleLink';
import { useLocale } from '@/hooks/useLocale';

const CONSENT_KEY = 'autofrance-cookie-consent';

type ConsentLevel = 'all' | 'necessary' | null;

export function CookieConsent() {
  // Start visible so banner is in SSR HTML (paints at FCP, not after hydration).
  // Inline <script> in <head> adds .cc-done class to hide it for returning users.
  // Inte inne i de inloggade delarna (ägaren 2026-10-07: "Vi kan inte ha cookie banners här inne"). Har
  // användaren inte valt får sidan där bara nödvändiga cookies — Consent Mode står kvar på nekat tills ett val
  // görs på den öppna sajten, så inget spårar utan samtycke.
  const pathname = usePathname() ?? '';
  const iInloggat = /^\/(en\/)?(foretag\/(konto|forsakring|mekaniker)|konto)(\/|$)/.test(pathname);
  const [visible, setVisible] = useState(!iInloggat);
  const bannerRef = useRef<HTMLDivElement>(null);
  const { locale } = useLocale();
  const isSe = locale === 'sv';

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY) as ConsentLevel;
    setVisible(!consent && !iInloggat);
  }, [iInloggat]);

  // Bannern är position:fixed längst ned och täcker därför alltid det som råkar ligga
  // där. I kassan blev det Förnamn och Efternamn — de första obligatoriska fälten —
  // vilket mättes upp 2026-09-02. Vi reserverar lika mycket utrymme längst ned på
  // sidan som bannern tar, så den lägger sig över tomrum i stället för över innehåll.
  //
  // OBS att detta bara löser DOKUMENTETS slut. En fixed ruta täcker det som ligger
  // nederst i VYN vid varje scrollposition, och där hjälper padding ingenting — se
  // höjdkommentaren vid layouten nedan.
  useEffect(() => {
    if (!visible) {
      document.body.style.paddingBottom = '';
      return;
    }
    const apply = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      document.body.style.paddingBottom = h ? `${h}px` : '';
    };
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro && bannerRef.current) ro.observe(bannerRef.current);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
    };
  }, [visible]);

  const handleAccept = (level: 'all' | 'necessary') => {
    localStorage.setItem(CONSENT_KEY, level);
    // Hide banner via injected style (avoids modifying <html> attributes which breaks React hydration)
    const s = document.createElement('style');
    s.textContent = '.cc-banner{display:none!important}';
    document.head.appendChild(s);

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      const granted = level === 'all' ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        analytics_storage: granted,
        ad_storage: granted,
        ad_user_data: granted,
        ad_personalization: granted,
      });
    }

    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';
    fetch(`${apiUrl}/consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ choice: level }),
    }).catch(() => {});

    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div ref={bannerRef} className="cc-banner fixed inset-x-0 z-[100] pointer-events-none"
      // --af-bottom-bar sätts av sidor som har en egen fast bottenrad (kassans
      // betalrad). Utan detta la sig bannern ovanpå den. 3.25rem är den mobila
      // bottennavigeringens höjd, som gäller på alla andra sidor.
      style={{ bottom: 'calc(var(--af-bottom-bar, 3.25rem) + env(safe-area-inset-bottom))' }}>
      {/* Höjden är inte kosmetik. Mätt 2026-09-08 på Moto G Power (412x823): med
          texten staplad ÖVER knapparna blev rutan 121 px och täckte 75 % av
          "Lägg i varukorg" på den mest besökta produktsidan, 78 % av
          antalsväljaren och 100 % av "Filter" plus de två översta träffarna på
          /sok. Varje förstagångsbesökare på mobil mötte det, och det är i princip
          alla — hade de accepterat vore rutan borta. Samma vecka la 27 av 918
          sessioner något i korgen.

          Därför ligger text och knappar på SAMMA rad även på mobil. Knapparna
          behåller 44 px (Lighthouse flaggar mindre träffytor) så höjden styrs av
          dem, inte av staplingen. */}
      <div className="pointer-events-auto bg-background/98 backdrop-blur-md border-t shadow-lg px-3 py-2 sm:px-4 sm:py-3">
        <div className="max-w-4xl mx-auto flex flex-row items-center gap-2 sm:gap-4">
          <p className="text-xs sm:text-sm text-muted-foreground flex-1 leading-snug">
            {isSe ? 'Vi använder cookies för varukorg, fordon och sökhistorik.' : 'We use cookies for cart, vehicle and search history.'}{' '}
            <Link href="/cookies" className="text-primary hover:underline">{isSe ? 'Läs mer' : 'Read more'}</Link>
          </p>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => handleAccept('all')}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors min-h-[44px]"
            >
              {isSe ? 'Acceptera' : 'Accept'}
            </button>
            <button
              onClick={() => handleAccept('necessary')}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md border border-input bg-background hover:bg-accent transition-colors min-h-[44px]"
            >
              {isSe ? 'Bara nödvändiga' : 'Necessary only'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
